import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { BadgeModule } from 'primeng/badge';
import { AvatarModule } from 'primeng/avatar';

// User Session interfaces
export interface IUserSession {
  sessionId: string;
  userId: string;
  firstName: string;
  lastName: string;
  tenantName: string;
  sessionStarted: string;
  sessionStatus: 'active' | 'inactive';
  lastActivity?: string;
  ipAddress?: string;
  userAgent?: string;
  deviceInfo?: string;
  location?: string;
}

@Component({
  selector: 'app-user-sessions',
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    ButtonModule,
    BadgeModule,
    AvatarModule
  ],
  templateUrl: './user-sessions.html',
  styleUrl: './user-sessions.css'
})
export class UserSessions implements OnInit {
  sessions: IUserSession[] = [
    {
      sessionId: 's1',
      userId: '1',
      firstName: 'Raveendar',
      lastName: 'Kumar',
      tenantName: 'SimplyGenie Technologies',
      sessionStarted: '2024-01-15T10:30:00Z',
      sessionStatus: 'active',
      lastActivity: '2024-01-15T14:45:00Z',
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
      deviceInfo: 'Windows 10, Chrome 120.0',
      location: 'Bangalore, India'
    },
    {
      sessionId: 's2',
      userId: '2',
      firstName: 'Priya',
      lastName: 'Sharma',
      tenantName: 'SimplyGenie Technologies',
      sessionStarted: '2024-01-15T09:15:00Z',
      sessionStatus: 'active',
      lastActivity: '2024-01-15T13:20:00Z',
      ipAddress: '192.168.1.101',
      userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36',
      deviceInfo: 'macOS Monterey, Safari 17.1',
      location: 'Mumbai, India'
    },
    {
      sessionId: 's3',
      userId: '3',
      firstName: 'Amit',
      lastName: 'Singh',
      tenantName: 'SimplyGenie Technologies',
      sessionStarted: '2024-01-15T08:45:00Z',
      sessionStatus: 'inactive',
      lastActivity: '2024-01-15T12:00:00Z',
      ipAddress: '192.168.1.102',
      userAgent: 'Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36',
      deviceInfo: 'Ubuntu 22.04, Firefox 121.0',
      location: 'Delhi, India'
    },
    {
      sessionId: 's4',
      userId: '1',
      firstName: 'Raveendar',
      lastName: 'Kumar',
      tenantName: 'Virtukart Solutions',
      sessionStarted: '2024-01-14T16:20:00Z',
      sessionStatus: 'inactive',
      lastActivity: '2024-01-14T18:30:00Z',
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
      deviceInfo: 'Windows 10, Chrome 120.0',
      location: 'Bangalore, India'
    },
    {
      sessionId: 's5',
      userId: '4',
      firstName: 'Sneha',
      lastName: 'Patel',
      tenantName: 'SimplyGenie Technologies',
      sessionStarted: '2024-01-14T11:10:00Z',
      sessionStatus: 'inactive',
      lastActivity: '2024-01-14T15:45:00Z',
      ipAddress: '192.168.1.103',
      userAgent: 'Mozilla/5.0 (iPhone; CPU iPhone OS 17_2 like Mac OS X) AppleWebKit/605.1.15',
      deviceInfo: 'iPhone 15, Safari Mobile',
      location: 'Chennai, India'
    }
  ];

  selectedSession: IUserSession | null = null;
  drawerOpen = false;

  // Filter and search
  searchTerm = '';
  filterStatus = 'all';
  filterTenant = 'all';

  // Filter options
  statusOptions = [
    { label: 'All Status', value: 'all' },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' }
  ];

  tenantOptions = [
    { label: 'All Tenants', value: 'all' },
    { label: 'SimplyGenie Technologies', value: 'SimplyGenie Technologies' },
    { label: 'Virtukart Solutions', value: 'Virtukart Solutions' }
  ];

  ngOnInit() {
    // Initialize component
  }

  get filteredSessions(): IUserSession[] {
    return this.sessions.filter(session => {
      const matchesSearch = 
        session.firstName.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        session.lastName.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        session.tenantName.toLowerCase().includes(this.searchTerm.toLowerCase());
      
      const matchesStatus = this.filterStatus === 'all' || session.sessionStatus === this.filterStatus;
      const matchesTenant = this.filterTenant === 'all' || session.tenantName === this.filterTenant;
      
      return matchesSearch && matchesStatus && matchesTenant;
    });
  }

  selectSession(session: IUserSession) {
    this.selectedSession = session;
    this.drawerOpen = true;
  }

  closeDrawer() {
    this.drawerOpen = false;
    this.selectedSession = null;
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }

  getInitials(firstName: string, lastName: string): string {
    return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
  }

  formatDateTime(dateString: string): string {
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  getSessionDuration(startTime: string, lastActivity?: string): string {
    const start = new Date(startTime);
    const end = lastActivity ? new Date(lastActivity) : new Date();
    const diffMs = end.getTime() - start.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffHours > 0) {
      return `${diffHours}h ${diffMinutes}m`;
    } else {
      return `${diffMinutes}m`;
    }
  }

  getTotalActiveSessions(): number {
    return this.sessions.filter(session => session.sessionStatus === 'active').length;
  }

  getTotalSessions(): number {
    return this.sessions.length;
  }

  getUniqueUsers(): number {
    const uniqueUserIds = new Set(this.sessions.map(session => session.userId));
    return uniqueUserIds.size;
  }

  getUniqueTenants(): number {
    const uniqueTenants = new Set(this.sessions.map(session => session.tenantName));
    return uniqueTenants.size;
  }

  terminateSession(session: IUserSession) {
    // Terminate session logic here
    console.log('Terminating session:', session.sessionId);
    session.sessionStatus = 'inactive';
  }

  terminateAllSessions(userId: string) {
    // Terminate all sessions for a user
    this.sessions.forEach(session => {
      if (session.userId === userId) {
        session.sessionStatus = 'inactive';
      }
    });
  }
}